import {
  SENSITIVITY_TIERS,
  SENSITIVITY_UNCLASSIFIED_COLOR,
  SENSITIVITY_UNCLASSIFIED_ID,
  sensitivityRank,
} from '@dawn/feature-config/widgets';
import {
  TopIdentitiesByFileAccessBody,
  type TopIdentitiesByFileAccessRow,
  type TopIdentitiesByFileAccessSensitivitySegment,
} from '@dawn/ui';

import type { WidgetConfig } from '@/config/dashboard.types';
import { useDrillToAccessDiscovery } from '@/hooks/use-drill-to-access-discovery';

import { WidgetShell } from '../WidgetShell';

const VALID_USER_TYPES = new Set<string>(['HI', 'NHI']);

// Sensitivity-id -> fillColor lookup. The API response's SensitivityCount carries
// no `color` field (apps/dashboard/api/src/features/dashboard/responses/top-identities.response.ts).
// Resolve color via the cross-app SENSITIVITY_TIERS registry. Lookup misses fall
// through to a single neutral color so the body always renders. Per Durable Decision #13.
// The warehouse emits `unclassified` in this breakdown alongside the five tier
// ids, so it is mapped here to the same grey the ring and the pipe popover use.
const SENSITIVITY_COLOR_BY_ID: ReadonlyMap<string, string> = new Map<string, string>([
  ...SENSITIVITY_TIERS.map((t) => [t.id, t.fillColor] as const),
  [SENSITIVITY_UNCLASSIFIED_ID, SENSITIVITY_UNCLASSIFIED_COLOR],
]);
const FALLBACK_SENSITIVITY_COLOR = '#D9D9D9' as const;
interface ResponseSensitivityCount {
  sensitivityId: string;
  label: string;
  count: number;
}

interface ResponseTaxonomyRef {
  id: string;
  label: string;
}

interface ResponseRow {
  id: string;
  actor: string;
  userType: 'HI' | 'NHI';
  /** Absent when the source has no privilege signal; never defaulted to false. */
  isPrivileged?: boolean;
  filesAccessed: number;
  sensitivityBreakdown: ResponseSensitivityCount[];
  documentTopics: ResponseTaxonomyRef[];
  regulationTopics: ResponseTaxonomyRef[];
  isHighlighted?: boolean;
}

interface ResponseData {
  rows: ResponseRow[];
  /** The card's cutoff; a drill ends the grid there. Absent on an older API. */
  asOf?: string;
}

function narrowSensitivityCount(value: unknown): ResponseSensitivityCount | null {
  if (value === null || typeof value !== 'object') return null;
  const s = value as Record<string, unknown>;
  if (typeof s.sensitivityId !== 'string' || s.sensitivityId.length === 0) return null;
  if (typeof s.label !== 'string') return null;
  if (typeof s.count !== 'number' || !Number.isFinite(s.count) || s.count < 0) return null;
  return s as unknown as ResponseSensitivityCount;
}

function narrowTaxonomyRef(value: unknown): ResponseTaxonomyRef | null {
  if (value === null || typeof value !== 'object') return null;
  const t = value as Record<string, unknown>;
  if (typeof t.id !== 'string' || t.id.length === 0) return null;
  if (typeof t.label !== 'string') return null;
  return t as unknown as ResponseTaxonomyRef;
}

function narrowRow(value: unknown): ResponseRow | null {
  if (value === null || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  if (typeof r.id !== 'string' || r.id.length === 0) return null;
  if (typeof r.actor !== 'string' || r.actor.length === 0) return null;
  if (typeof r.userType !== 'string' || !VALID_USER_TYPES.has(r.userType)) return null;
  if (r.isPrivileged !== undefined && typeof r.isPrivileged !== 'boolean') return null;
  if (
    typeof r.filesAccessed !== 'number' ||
    !Number.isFinite(r.filesAccessed) ||
    r.filesAccessed < 0
  ) {
    return null;
  }
  if (!Array.isArray(r.sensitivityBreakdown)) return null;
  for (const s of r.sensitivityBreakdown as unknown[]) {
    if (narrowSensitivityCount(s) === null) return null;
  }
  if (!Array.isArray(r.documentTopics)) return null;
  for (const t of r.documentTopics as unknown[]) {
    if (narrowTaxonomyRef(t) === null) return null;
  }
  if (!Array.isArray(r.regulationTopics)) return null;
  for (const t of r.regulationTopics as unknown[]) {
    if (narrowTaxonomyRef(t) === null) return null;
  }
  if (r.isHighlighted !== undefined && typeof r.isHighlighted !== 'boolean') return null;
  return r as unknown as ResponseRow;
}

export function narrowTopIdentitiesData(input: unknown): ResponseData | null {
  if (input === null || typeof input !== 'object') return null;
  const candidate = input as Record<string, unknown>;
  if (!Array.isArray(candidate.rows)) return null;
  const seenIds = new Set<string>();
  for (const row of candidate.rows as unknown[]) {
    const r = narrowRow(row);
    if (r === null) return null;
    if (seenIds.has(r.id)) return null; // hard-reject on duplicate row id (Decision #10)
    seenIds.add(r.id);
  }
  if (candidate.asOf !== undefined && typeof candidate.asOf !== 'string') return null;
  return candidate as unknown as ResponseData;
}

function resolveSensitivityColor(id: string): string {
  return SENSITIVITY_COLOR_BY_ID.get(id) ?? FALLBACK_SENSITIVITY_COLOR;
}

function mapSensitivitySegment(
  s: ResponseSensitivityCount
): TopIdentitiesByFileAccessSensitivitySegment {
  return {
    sensitivityId: s.sensitivityId,
    label: s.label,
    count: s.count,
    color: resolveSensitivityColor(s.sensitivityId),
  };
}

export function TopIdentitiesWidget({ config }: { config: WidgetConfig }): JSX.Element {
  const drillToAccessDiscovery = useDrillToAccessDiscovery(config.id);

  return (
    <WidgetShell
      config={config}
      renderData={(envelopeData) => {
        const data = narrowTopIdentitiesData(envelopeData);
        if (!data) return null;
        // Every drill ends the grid's window at this card's cutoff.
        const drill = (link: Parameters<typeof drillToAccessDiscovery>[0]) =>
          drillToAccessDiscovery({ ...link, asOf: data.asOf });

        const rows: TopIdentitiesByFileAccessRow[] = data.rows.map((r) => ({
          id: r.id,
          actor: r.actor,
          userType: r.userType,
          isPrivileged: r.isPrivileged,
          filesAccessed: r.filesAccessed,
          // Bar segments and the hover card's rows follow tier order, whatever
          // order the warehouse sent.
          sensitivityBreakdown: [...r.sensitivityBreakdown]
            .sort((a, b) => sensitivityRank(a.sensitivityId) - sensitivityRank(b.sensitivityId))
            .map(mapSensitivitySegment),
          documentTopics: r.documentTopics.map((t) => ({ id: t.id, label: t.label })),
          regulationTopics: r.regulationTopics.map((t) => ({ id: t.id, label: t.label })),
          isHighlighted: r.isHighlighted,
        }));

        // A row id is dw's `principal_key`, the key the events grid filters by.
        return (
          <TopIdentitiesByFileAccessBody
            rows={rows}
            onRowClick={(row) => drill({ principal: { key: row.id, label: row.actor } })}
          />
        );
      }}
    />
  );
}
